import * as React from 'react';
import { isEmpty } from 'lodash';
import * as I18n from 'i18n-js';
import * as classNames from 'classnames';
import CoreLink from '@reverbdotcom/commons/dist/components/core_link';
import AdTag from '@reverbdotcom/commons/dist/components/ads/ad_tag';
import { MParticleEventName, MParticleEvent } from '@reverbdotcom/commons/dist/elog/mparticle_types';

const COMPONENT_NAME = 'PromotionalBannerTop';

export interface ISiteBanner {
  id: string;
  heading: string;
  subheading: string;
  callToAction: string;
  downtime: boolean;
  url: {
    href: string;
  };
  mobileWebUrl: {
    href: string;
  };
  contentSponsorship: boolean;
}

interface PromotionalBannerTopProps {
  siteBanner: ISiteBanner;
}

function buildClickEvent(siteBanner: ISiteBanner): MParticleEvent {
  return {
    eventName: MParticleEventName.SiteBanner,
    componentName: COMPONENT_NAME,
    entityId: siteBanner.id,
    isAd: !!siteBanner.contentSponsorship,
  };
}

function SponsorshipContent() {
  return (
    <span className="promotional-banner-top__info__ad">
      <AdTag inline small />
    </span>
  );
}

function MobilePromotionalBanner({ siteBanner }: PromotionalBannerTopProps) {
  const classes = classNames(
    'promotional-banner-top',
    'd-none',
    'mobile-d-block',
  );

  return (
    <div className={classes}>
      <CoreLink
        to={siteBanner.mobileWebUrl.href}
        clickEvent={buildClickEvent(siteBanner)}
      >
        <aside className="promotional-banner-top__inner">
          <div className="promotional-banner-top__info">
            <b>{siteBanner.heading}</b>
            <span className="promotional-banner-top__info__subheading">
              {siteBanner.subheading}
            </span>
            {siteBanner.contentSponsorship && <SponsorshipContent />}
          </div>
          <div className="promotional-banner-top__action">
            {I18n.t('discovery.siteBanner.sale.getAppAnd', { call_to_action: I18n.t('discovery.siteBanner.sale.viewSale') })}
          </div>
        </aside>
      </CoreLink>
    </div>
  );
}

function DownTimePromotionalBanner({ siteBanner }: PromotionalBannerTopProps) {
  const classes = classNames(
    'promotional-banner-top',
    'promotional-banner-top--downtime',
  );
  return (
    <div className={classes}>
      <aside className="promotional-banner-top__inner">
        <div className="promotional-banner-top__info">
          <b>{siteBanner.heading}</b>
          {siteBanner.subheading}
        </div>
      </aside>
    </div>
  );
}

export function PromotionalBannerTop(props: PromotionalBannerTopProps) {
  if (!props.siteBanner || isEmpty(props.siteBanner)) {
    return null;
  }
  const { siteBanner } = props;
  const mobileWebUrl = siteBanner.url.href !== siteBanner.mobileWebUrl.href;
  const classes = classNames(
    'promotional-banner-top',
    { 'mobile-d-none': mobileWebUrl },
  );

  if (siteBanner.downtime) {
    return <DownTimePromotionalBanner siteBanner={siteBanner} />;
  }

  return (
    <>
      <div className={classes}>
        <CoreLink
          to={siteBanner.url.href}
          clickEvent={buildClickEvent(siteBanner)}
        >
          <aside className="promotional-banner-top__inner">
            <div className="promotional-banner-top__info">
              <b>{siteBanner.heading}</b>
              <span className="promotional-banner-top__info__subheading">
                {siteBanner.subheading}
              </span>
              {siteBanner.contentSponsorship && <SponsorshipContent />}
            </div>
            <div className="promotional-banner-top__action">
              {siteBanner.callToAction}
            </div>
          </aside>
        </CoreLink>
      </div>
      {mobileWebUrl && <MobilePromotionalBanner siteBanner={siteBanner} />}
    </>
  );
}

export default PromotionalBannerTop;
